import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import {
  InputNumber,
  InputNumberValueChangeEvent,
} from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { useEffect, useMemo, useRef, useState } from "react";
import "./index.css";
import { useTripStore } from "presentation/stores/use-trip-store";
import { useTripDestinationStore } from "presentation/stores/use-trip-destination-store";
import { Tripsheet } from "domain/entities/tripsheet";
import { TripInputFilter } from "domain/models/trip-input-filter";
import { DistanceInputFilter } from "domain/models/distance-input-filter";
import { useTripsByFilter, useUpdateTripMutation } from "presentation/hooks/use-query-trip";
import { useTripLocationsByFilter } from "presentation/hooks/use-query-distance";
import { Parameter } from "domain/entities/parameter";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Toast } from "primereact/toast";
import { confirmDialog } from "primereact/confirmdialog";
import { EMAIL } from "domain/enum/email";
import Spinner from "presentation/components/Shared/Spinner";
import { formatDateToPT } from "presentation/utils/formatDateToPT";
import { DistanceTypeEnum } from "domain/enum/distanceType";
import { Trip } from "domain/entities/trip";

interface ParamProps {
  isDisable: boolean;
  tripsheetToAnalyse?: Tripsheet
}

interface TripModel {
  id: string;
  tripsheet: Pick<Tripsheet, "id">;
  client: string,
  reason: string,
  locationDestination: string,
  distanceKm: number,
  tripValue: number,
  dateTrip: Date,
  modifiedAt: Date,
  lastModifiedBy: string;

}

const TripsheetMonthForm: React.FC<ParamProps> = (params: ParamProps) => {
  const toast = useRef<Toast>(null);
  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message: message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  const { trip: trips, setTrip } = useTripStore();
  const { tripDestination: tripDestinations, setTripDestination } = useTripDestinationStore();
  const { email: userEmployeeEmail, userPermissions } = useAuthStore();
  const [tripsheetOrigin, setTripsheetOrigin] = useState("");

  const { data: tripsData, isError: isErrorTrips, isLoading: isLoadingTrips, error: errorTrips, refetch: refetchExistentTrips } = useTripsByFilter({
    tripsheetId: params.tripsheetToAnalyse?.id,
    sortBy: "dateTrip",
    sortOrder: "asc",
    page: 1,
    limit: 100
  } as TripInputFilter);

  const { data: tripDestinationData, isError: isErrorDestinations, isLoading: isLoadingDestinations, error: errorDestinations, refetch: refetchExistentDestinations } = useTripLocationsByFilter({
    locationOrigin: params.tripsheetToAnalyse?.locationOrigin,
    type: DistanceTypeEnum.MAPA_KM,
    sortBy: "locationDestination",
    sortOrder: "asc",
    page: 1,
    limit: 500
  } as DistanceInputFilter);

  const {
    mutate: updateTripMutation,
    mutateAsync: updateTripMutationAsync,
    error: updatingTripError,
    isError: isUpdatingTripError,
    isSuccess: isUpdatingTripSuccess,
    isPending: isUpdatingTrips
  } = useUpdateTripMutation();

  const totalDistance = useMemo(() => {
    return trips.reduce((acc, trip) => acc + trip.distanceKm, 0);
  }, [trips]);

  const totalAmount = useMemo(() => {
    return trips.reduce((acc, trip) => acc + trip.tripValue, 0);
  }, [trips]);

  useEffect(() => {
    const fn = async () => {
      const newTrips = await refetchExistentTrips();
      const newTripsWithOrigin: (Trip & { locationOrigin: string })[] = [];

      for (const tr of newTrips.data?.data ?? []) {
        const tripWithOrigin: Trip & { locationOrigin: string } = {
          ...tr,
          locationOrigin:
            tr.reason.toLowerCase() === "supporttrip"
              ? tr.client
              : "(*1)",
        };

        if (tr.reason.toLowerCase() !== "supporttrip" && !tripsheetOrigin) {
          setTripsheetOrigin(tr.tripsheet?.locationOriginFullString ?? "");
        }

        newTripsWithOrigin.push(tripWithOrigin);
      }

      setTrip(newTripsWithOrigin);
      refetchExistentDestinations();
    };

    fn();
  }, [params.tripsheetToAnalyse]);


  useEffect(() => {
    if (tripDestinationData?.data) {
      const tripDestinationsMap = new Map<string, Parameter>();

      tripDestinationData.data.forEach((item) => {
        const key = item.locationDestination?.trim();
        if (key && !tripDestinationsMap.has(key)) {
          tripDestinationsMap.set(key, {
            value: item.locationDestination,
            id: item.locationDestination,
            type: "tripDestination",
            createdAt: item.createdAt,
            modifiedAt: item.modifiedAt,
          });
        }
      });
      const uniqueTripDestinations = Array.from(tripDestinationsMap.values());
      setTripDestination(uniqueTripDestinations);
    }
  }, [tripDestinationData]);

  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        type="text"
        value={options.value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          options.editorCallback!(e.target.value)
        }
        style={{ width: "14vw" }}
      />
    );
  };
  const numberEditor = (options: ColumnEditorOptions) => {
    return (
      <InputNumber
        value={options.value}
        onValueChange={(e: InputNumberValueChangeEvent) =>
          options.editorCallback!(e.value)
        }
      />
    );
  };

  const dateEditor = (options: ColumnEditorOptions) => {

    const value = options.value ? new Date(options.value) : null;

    return (
      <Calendar
        value={value}
        onChange={(e) => options.editorCallback!(e.value)}
        dateFormat="dd/mm/yy"
        showIcon
        style={{ width: "10vw" }}
      />
    );
  };

  const cityEditor = (options: ColumnEditorOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={tripDestinations}
        onChange={(e: DropdownChangeEvent) => options.editorCallback!(e.value)}
        optionLabel="value"
        placeholder="Selecione o destino"
        itemTemplate={(option) => <span>{option.value}</span>}
        style={{ width: "14vw" }}
      />
    );
  };

  const allowEdit = (rowData: any) => {
    if (rowData[0])
      return rowData[0].reason?.toLowerCase() !== "supporttrip";
    return false;
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const _trips = [...trips];
    const { newData, index } = e;

    _trips[index] = {
      ..._trips[index],
      ...newData,
      dateTrip: new Date(newData.dateTrip),
      distanceKm: Number(newData.distanceKm) || 0,
      tripValue: Number(newData.tripValue) || 0,
    };
    setTrip([..._trips]);
  };


  const handleSave = async () => {
    for (const trip of trips) {
      trip.lastModifiedBy = userEmployeeEmail ?? EMAIL;
      const fn = async () => {
        await updateTripMutationAsync(trip);
      }
      await fn();
    }
  };

  useEffect(() => {
    if (isUpdatingTripError) {
      let message = [];
      message.push(updatingTripError.message);
      message = updatingTripError.response.data.metadata.errors;
      console.log("message", message);
      message.map((item: string) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: item || "Ocorreu um erro inesperado",
          life: 2000,
        });
      });
    }
  }, [updatingTripError, isUpdatingTripError]);

  useEffect(() => {
    if (isUpdatingTripSuccess) {
      const successMessage = "Trips atualizadas com sucesso";

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: successMessage,
        life: 2000,
      });
    }
  }, [isUpdatingTripSuccess]);

  return (
    <div>

      {isUpdatingTrips && <Spinner />}

      {!isUpdatingTrips &&

        <div className="default-grid header-spacing">
          <div className="default-4-cols">
            <DataTable
              scrollable
              scrollHeight="50vh"
              editMode="row"
              onRowEditComplete={onRowEditComplete}
              value={trips || []}
              // lazy
              // paginator
              // first={(lazyParams.page - 1) * lazyParams.limit}
              // rows={lazyParams.limit}
              // totalRecords={employees?.pagination?.count || 0}
              // loading={isLoading}
              // onPage={(e: DataTablePageEvent) =>
              //   setLazyParams({
              //     ...lazyParams,
              //     page: e.page ? e.page + 1 : 1,
              //     limit: e.rows,
              //   })
              // }
              size="small"
              resizableColumns
              columnResizeMode="expand"
              // rowsPerPageOptions={[2, 10, 25, 50]}
              // paginatorLeft
              dataKey="id"
              style={{ fontSize: "0.8rem" }}
            >
              <Column
                field="dateTrip"
                header={"Data"}
                body={(rowData) => formatDateToPT(rowData.dateTrip)}
                editor={(options) => dateEditor(options)}
                style={{ width: "10%" }}
              />
              <Column
                field="locationOrigin"
                header={"Origem"}
                // editor={(options) => textEditor(options)}
                style={{ width: "20%" }}
              />
              <Column
                field="locationDestination"
                header={"Destino"}
                editor={(options) => cityEditor(options)}
                style={{ width: "20%" }}
              />
              <Column
                field="reason"
                header={"Motivo"}
                editor={(options) => textEditor(options)}
                style={{ width: "20%" }}
                body={(rowData) => 
                  rowData.reason.toLowerCase() === "supporttrip" ?
                    "Intervenção":
                      rowData.reason
                  }
              />

              <Column
                field="distanceKm"
                body={(rowData) => rowData.distanceKm % 1 === 0
                  ? rowData.distanceKm.toFixed(1)
                  : rowData.distanceKm.toString()
                }
                header={"KMs"}
                align={"right"}
                footer={totalDistance % 1 === 0
                  ? totalDistance.toFixed(1)
                  : totalDistance.toString()}
                editor={(options) => numberEditor(options)}
                style={{ width: "8%" }}
              />
              <Column
                field="tripValue"
                align={"right"}
                body={(rowData) => rowData.tripValue.toFixed(2)}
                header={"Valor (em €)"}
                footer={totalAmount.toFixed(2)}
                editor={(options) => numberEditor(options)}
                style={{ width: "8%" }}
              />
              <Column
                rowEditor={(rowData) => allowEdit(rowData)}
                headerStyle={{ width: "14%", minWidth: "8rem" }}
                bodyStyle={{ textAlign: "center" }}
              ></Column>
            </DataTable>
          </div>

          {tripsheetOrigin && (
            <span style={{ fontSize: "0.8rem", whiteSpace: "nowrap"  }}>
              (*1) {tripsheetOrigin}
            </span>
            )
          }
          
         

          <div className="default-4-cols button-align">
            <Button
              label="Guardar"
              className="button-toolbar "
              onClick={() =>
                confirmAction(
                  "Deseja atualizar os valores das viagens?",
                  handleSave
                )
              }
            />
          </div>
        </div>

      }

      <Toast ref={toast} position="bottom-right" />
      {/*<ConfirmDialog />*/}

    </div>
  );
};
export default TripsheetMonthForm;
